/* tslint:disable:no-inferrable-types no-trailing-whitespace import-spacing typedef-whitespace align whitespace quotemark */

import { DocumentWindow }          from './document.window';
import { DocumentWindowManager }   from './document.window.manager';
import { Logger }                  from '@/util/logger';
import { BlobService }             from '@/services/blob.service';
import { AuthenticationService}    from '@/services/authentication.service';
import { IdeService}               from '@/services/ide.service';
import { TSOEditorMetadata }       from './t.s.o.editor.metadata';
import { TSOEditorComponent }      from '@/components/t.s.o.editor.component';

import * as $                      from 'jquery';
import 'gasparesganga-jquery-loading-overlay'; /* https://gasparesganga.com/labs/jquery-loading-overlay/ */

import { first                   } from "rxjs/operators";
import { forkJoin                } from 'rxjs';

import {
  ComponentFactoryResolver,
  ViewContainerRef,
  EmbeddedViewRef
}                                  from '@angular/core';
import {empty} from "@/util/util";

/**
 *
 * @class TSOEditorWindow
 *
 * @note this class uses dynamic component creation to host an Angular component inside a non-Angular
 * object (i.e. DocumentWindow).
 *
 * @brief window for editing train slow orders (TSO) for a scenario
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      July 2020
 *
 */
export class TSOEditorWindow extends DocumentWindow {

  /** access to the virtual file system */
  private blobber:              BlobService = null;

  /** access to user authentication */
  private auth:       AuthenticationService = null;

  /** access to user authentication */
  private ide:                   IdeService = null;

  /** the document manager we are a tab  of */
  private manager:    DocumentWindowManager = null;

  /** the current scenario (blob key) */
  private key:                       string = '';

  /** the full text of the scenario (if we have it) */
  private text:                      string = '';

  /** the Angular component that is the actual scenario browser...which we are hosting */
  private editor:        TSOEditorComponent = null;

  /** standard constructor */
  constructor(
    meta:    TSOEditorMetadata,
    manager: DocumentWindowManager
  ) {

    super(manager, meta, manager.layoutManager());

    Logger.debug('[TSOEditorWindow] constructing...');

    this.manager     = manager;
    this.auth        = this.manager.authService();
    this.blobber     = this.manager.blobService();
    this.ide         = this.manager.ideService();
    this.key         = meta.key;
    this.text        = meta.text;

    this.showEditor();
  }

  /** subclasses (implementing windows), must do something appropriate for the save action */
  public save() {

    this.editor.saveScenario();
  }

  /** bring up the editor on the current meta data */
  public showEditor() {

    Logger.debug(`[TSOEditorWindow] => ${this.key} ...`);

    const container: JQuery<HTMLElement> = $(`#${this.meta.id}`);

    if (!$(container) || !$(container).is('div')) {

      this.unReady();

      Logger.error(`[TSOEditorWindow] missing hosting container (${this.meta.id})!`);

      return ;
    }

    $(container).html(`
<div id="tso_editor_${this.meta.id}" class="tso-editor-wrapper">

</div>
`);

    /*
     * wait for the HTML to actually update with the component selector above, this won't create the
     * component, its just HTML :)  But, we can use Angular's component factory etc. to create the
     * component and effective "host" an Angular component inside our DocumentWindow (which isn't
     * Angular based).
     *
     */

    const checkExist = setInterval(() => {

      const dom: HTMLElement = document.getElementById(`tso_editor_${this.meta.id}`) as HTMLElement;

      if (dom) {

        clearInterval(checkExist);

        Logger.debug(`[TSOEditorWindow] inject tso editor component ...`);

        const    view: ViewContainerRef         = this.ide.view;
        const factory: ComponentFactoryResolver = this.ide.componentResolver;


        const creator      = factory.resolveComponentFactory(TSOEditorComponent);
        const componentRef = view.createComponent(creator);

        this.editor        = componentRef.instance  as TSOEditorComponent;

        const domElem      = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;

        /* ensure the new component is parented under our container */

        dom.appendChild(domElem);

        /*  can now use 'browser' as usual ... */


        if (!empty(this.text)) {

          /* we have the full text of scenario file, load directly ... */

          try {

            Logger.debug(`[TSOEditorWindow] loading from blob (sz=${this.text.length})...`);

            this.editor.fromText(this.text, this.key);

          } catch(error) {

            Logger.error(`[TSOEditorWindow] problem loading (direct) JSON: `, error);
          }

          this.updateFocus(this.key);

        } else {

          this.loadBlob(this.key);
        }
        this.editor.reloaded = (): void => {

          this.key   = '';
          this.title = '';
          this.text  = '';

          this.updateFocus(this.key);

          if (this.editor.scenario) {

            const label: string = this.editor.fileName;

            Logger.debug(`[TSOEditorWindow] content changed: ${label}`);

            let keyBase: string = label.substring(label.lastIndexOf('/') + 1);

            if (!keyBase || keyBase === '' || keyBase === label) {

              keyBase = label.substring(label.lastIndexOf('\\') + 1);

              if (!keyBase || keyBase === '' || keyBase === label) {

                keyBase = label;
              }
            }

            this.title = `${keyBase}`;

            if (/\.[^.\/]+$/.test(this.title)) {

              this.title = this.title.substring(0, this.title.lastIndexOf('.'));
            }

          } else {

            Logger.debug(`[TSOEditorWindow] no content.`);
          }

        };
      }

    }, 100);
  }

  private loadBlob(key: string) {

    if (key === '') {

      /* nothing to do yet */

      return ;
    }

    Logger.debug(`[TSOEditorWindow] loading: `, key);

    this.blobber.contents(key)
      .pipe(first())
      .subscribe(
        (content: string) => {

          try {

            Logger.debug(`[TSOEditorWindow] loading from blob (sz=${content.length})...`);

            this.editor.fromText(content, key);

          } catch(error) {

            Logger.error(`[TSOEditorWindow] problem loading JSON: `, error);
          }

          this.updateFocus(this.key);

        },
        (error) => {

          Logger.error(`[TSOEditorWindow] can't find content ${key}: `, error);
        });

  }

  /** update the focus to current */
  private updateFocus(key: string) {

    this.key    = key;

    let keyBase = key.substring(key.lastIndexOf('/') + 1);

    if (!keyBase || keyBase === '' ) {

      keyBase = key;
    }

    this.title  = `${keyBase}`;

    if (/\.[^.\/]+$/.test(this.title)) {

      this.title = this.title.substring(0, this.title.lastIndexOf('.'));
    }
  }

}
